import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Floor } from 'src/api/types';
import {
  toBuilding,
  toBuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import {
  FloorSelectItem,
  toFloor,
  toFloorSelectItem,
} from 'src/components/FloorSelect/FloorSelect';
import {
  OfficeSidesheetContent,
  OfficeSidesheetValues,
} from 'src/components/OfficeSidesheet/OfficeSidesheetContent';
import { toTown, toTownSelectItem } from 'src/components/TownSelect/TownSelect';
import { getIsMobile } from 'src/store/agent/selectors';
import { fetchFloors } from 'src/store/floors/services';
import { getRoomsState } from 'src/store/rooms/selectors';
import { roomsActions } from 'src/store/rooms/slice';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

const filterFloorOptions = (option: FloorSelectItem): boolean =>
  !option.value.isParking;

export const RoomOfficeSidesheetContent: FC = () => {
  const dispatch = useAppDispatch();

  const { town, building, floor } = useSelector(getRoomsState);
  const isMobile = useSelector(getIsMobile);

  const apply = useCallback(
    async (values: OfficeSidesheetValues) => {
      let firstFloor: Floor | undefined = undefined;

      if (values.floor) {
        firstFloor = toFloor(values.floor);
      } else {
        if (values.building) {
          const floors = await dispatch(
            fetchFloors({
              cached: true,
              buildingId: values.building.value.buildingId,
            }),
          ).unwrap();
          firstFloor = [...floors.values()][0];
        }
      }

      dispatch(roomsActions.setTown(toTown(values.town)));
      dispatch(roomsActions.setBuilding(toBuilding(values.building)));
      dispatch(roomsActions.setFloor(firstFloor));
      dispatch(sidesheetActions.close());
    },
    [dispatch],
  );

  const clear = useCallback(() => {
    dispatch(roomsActions.clearLocation());
    dispatch(sidesheetActions.close());
  }, [dispatch]);

  const initialValues = useMemo(
    (): OfficeSidesheetValues => ({
      town: toTownSelectItem(town),
      building: toBuildingSelectItem(building),
      floor: toFloorSelectItem(floor),
    }),
    [building, floor, town],
  );

  return (
    <OfficeSidesheetContent
      apply={apply}
      clear={clear}
      showFloor={isMobile}
      initialValues={initialValues}
      filterFloorOptions={filterFloorOptions}
    />
  );
};
